'use client'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { format } from 'date-fns'
import { CalendarRange, Clock, Heart, PawPrint, Pill, Scissors, Trash2, Truck, UtensilsCrossed } from 'lucide-react'

import { usePendingHotelRequests, useReservations } from '@/components/ReservationContext'
import { useReservationModal } from '@/pages/panel-interno/components/ReservationModalContext'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { ConfirmationDialog } from '@/shared/ui/confirmation-dialog'
import { formatTimeAgo } from '@/shared/utils/format'
import { HotelReservation, Pet } from '@monorepo/functions/src/types/reservations'
import { DriverService, ServiceType } from '@monorepo/functions/src/types/services'

export function HotelPendingRequests() {
    const { t } = useTranslation()
    const { updateReservation, deleteReservation } = useReservations()
    const { pendingReservations, budgets, isLoading } = usePendingHotelRequests()
    const [searchParams, setSearchParams] = useSearchParams()
    const { openReservationModal } = useReservationModal()
    const [reservationToDelete, setReservationToDelete] = useState<HotelReservation | null>(null)
    const [reservationToAccept, setReservationToAccept] = useState<HotelReservation | null>(null)

    const handleCheckAvailability = (reservation: HotelReservation) => {
        console.log('Checking availability for reservation:', reservation)
        const checkInDate = new Date(reservation.checkInDate)
        const dateStr = format(checkInDate, 'yyyy-MM-dd')

        // Mantener los parámetros existentes y añadir los nuevos
        const newParams = new URLSearchParams(searchParams)
        newParams.set('pendingReservationId', reservation.id)
        newParams.set('date', dateStr)
        newParams.set('tab', 'calendar')

        console.log('Setting search params:', Object.fromEntries(newParams.entries()))
        setSearchParams(newParams)
    }

    const getServiceIcon = (serviceType: ServiceType) => {
        switch (serviceType) {
            case 'driver':
                return <Truck className='h-4 w-4 text-blue-500' />
            case 'special_food':
                return <UtensilsCrossed className='h-4 w-4 text-orange-500' />
            case 'medication':
                return <Pill className='h-4 w-4 text-red-500' />
            case 'special_care':
                return <Heart className='h-4 w-4 text-pink-500' />
            case 'hairdressing':
                return <Scissors className='h-4 w-4 text-purple-500' />
            default:
                return null
        }
    }

    const formatServiceName = (serviceType: ServiceType) => {
        switch (serviceType) {
            case 'driver':
                return 'Servicio de chofer'
            case 'special_food':
                return 'Comida especial'
            case 'medication':
                return 'Medicación'
            case 'special_care':
                return 'Curas'
            case 'hairdressing':
                return 'Peluquería'
            default:
                return 'Servicio desconocido'
        }
    }

    const handleCardClick = (reservation: HotelReservation) => {
        openReservationModal(reservation)
    }

    const handleAcceptBudget = async (budget: HotelReservation) => {
        await updateReservation(budget.id, {
            status: 'confirmed',
        })
        setReservationToAccept(null)
    }

    const handleDeleteBudget = async (budget: HotelReservation) => {
        await deleteReservation(budget.id)
        setReservationToDelete(null)
    }

    const renderReservationCard = (reservation: HotelReservation) => {
        return (
            <Card
                key={reservation.id}
                className={`relative overflow-hidden ${reservation.status === 'budget' ? 'bg-[#F0F7FF]' : ''} cursor-pointer transition-shadow hover:shadow-md`}
                onClick={() => handleCardClick(reservation)}
            >
                {reservation.status === 'pending' && reservation.createdAt && (
                    <div className='absolute right-4 top-4 z-10 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50/80 px-2.5 py-1.5 shadow-sm'>
                        <Clock className='h-3.5 w-3.5 text-blue-600' />
                        <span className='text-xs font-medium text-blue-800'>
                            {(() => {
                                const timeAgo = formatTimeAgo(reservation.createdAt)
                                return timeAgo
                                    ? t('reservation.requestedAgo', 'Solicitada hace {{time}}', {
                                          time: timeAgo,
                                      })
                                    : null
                            })()}
                        </span>
                    </div>
                )}
                <div className='flex'>
                    <div className='flex w-64 shrink-0 flex-col justify-between space-y-6 bg-[#4B6BFB]/5 p-6'>
                        <div className='space-y-6'>
                            <div>
                                <CardTitle className='text-lg font-semibold'>{reservation.client.name}</CardTitle>
                                <div className='mt-1 space-y-1 text-sm text-muted-foreground'>
                                    <p>{reservation.client.phone}</p>
                                    <p>{reservation.client.email}</p>
                                </div>
                            </div>

                            <div className='space-y-2'>
                                <div className='flex items-center gap-2'>
                                    <Clock className='h-4 w-4' />
                                    <span className='text-sm text-muted-foreground'>
                                        {format(new Date(reservation.checkInDate), 'dd/MM/yyyy')} -{' '}
                                        {format(new Date(reservation.checkOutDate), 'dd/MM/yyyy')}
                                    </span>
                                </div>
                                {reservation.additionalServices.some(service => service.type === 'driver') && (
                                    <div className='flex items-center gap-2'>
                                        <Truck className='h-4 w-4 text-[#4B6BFB]' />
                                        <span className='text-sm text-muted-foreground'>
                                            {(() => {
                                                const transportService = reservation.additionalServices.find(
                                                    service => service.type === 'driver',
                                                ) as DriverService | undefined
                                                if (transportService && transportService.type === 'driver') {
                                                    switch (transportService.serviceType) {
                                                        case 'pickup':
                                                            return 'Recogida'
                                                        case 'dropoff':
                                                            return 'Entrega'
                                                        case 'both':
                                                            return 'Recogida y entrega'
                                                        default:
                                                            return 'Transporte'
                                                    }
                                                }
                                                return 'Transporte'
                                            })()}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <CardContent className='flex-1 p-6'>
                        <div className='flex h-full flex-col'>
                            <div className='flex-grow space-y-6'>
                                {/* Mascotas y sus servicios */}
                                {reservation.pets.map((pet: Pet, petIndex: number) => {
                                    // Filtrar servicios para esta mascota
                                    const petServices = reservation.additionalServices.filter(
                                        service =>
                                            service.type !== 'driver' &&
                                            'petIndex' in service &&
                                            service.petIndex === petIndex, // Excluir servicio de transporte
                                    )

                                    return (
                                        <div
                                            key={petIndex}
                                            className={`${petIndex < reservation.pets.length - 1 ? 'border-b border-border' : ''}`}
                                        >
                                            <div className='py-4'>
                                                <div className='grid grid-cols-[auto,1fr] gap-6'>
                                                    <div className='flex items-start gap-2'>
                                                        <PawPrint className='mt-1 h-4 w-4 text-[#4B6BFB]' />
                                                        <div>
                                                            <p className='font-medium'>{pet.name}</p>
                                                            <p className='text-sm text-muted-foreground'>
                                                                {pet.breed} · {pet.size} · {pet.weight}kg ·{' '}
                                                                {pet.sex === 'M' ? 'Macho' : 'Hembra'} ·{' '}
                                                                {pet.isNeutered ? 'Castrado/a' : 'No castrado/a'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className='flex flex-wrap gap-4'>
                                                        {petServices.map((service, index) => {
                                                            const icon = getServiceIcon(service.type as ServiceType)
                                                            return icon ? (
                                                                <div key={index} className='flex items-center gap-2'>
                                                                    {icon}
                                                                    <span className='text-sm text-muted-foreground'>
                                                                        {formatServiceName(service.type as ServiceType)}
                                                                    </span>
                                                                </div>
                                                            ) : null
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}

                                <div className='flex items-center justify-between border-t pt-6'>
                                    <span className='font-semibold'>Precio Total</span>
                                    <span className='text-lg font-bold'>
                                        {(reservation.totalPrice ?? 0).toLocaleString('es-ES', {
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 0,
                                        })}
                                        €
                                    </span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </div>

                <div className='flex justify-end gap-2 border-t bg-gray-50 p-4'>
                    {reservation.status === 'budget' ? (
                        <>
                            <Button
                                variant='destructive'
                                onClick={e => {
                                    e.stopPropagation()
                                    setReservationToDelete(reservation)
                                }}
                            >
                                <Trash2 className='mr-2 h-4 w-4' />
                                Eliminar
                            </Button>
                            <Button
                                className='bg-[#4B6BFB] text-white hover:bg-[#4B6BFB]/90'
                                onClick={e => {
                                    e.stopPropagation()
                                    handleCheckAvailability(reservation)
                                }}
                            >
                                <CalendarRange className='mr-2 h-4 w-4' />
                                Comprobar Disponibilidad
                            </Button>
                        </>
                    ) : (
                        <Button
                            className='bg-[#4B6BFB] text-white hover:bg-[#4B6BFB]/90'
                            onClick={e => {
                                e.stopPropagation()
                                handleCheckAvailability(reservation)
                            }}
                        >
                            <CalendarRange className='mr-2 h-4 w-4' />
                            Comprobar Disponibilidad
                        </Button>
                    )}
                </div>
            </Card>
        )
    }

    return (
        <div className='space-y-8'>
            {isLoading ? (
                <div className='py-8 text-center text-muted-foreground'>Cargando solicitudes pendientes...</div>
            ) : (
                <>
                    {pendingReservations.length === 0 && budgets.length === 0 ? (
                        <Alert variant='info' className='mt-2'>
                            <CalendarRange className='h-4 w-4' />
                            <AlertTitle>Sin solicitudes pendientes</AlertTitle>
                            <AlertDescription>
                                No hay reservas ni presupuestos pendientes de gestionar.
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <>
                            {/* Pending Requests Section */}
                            <div className='space-y-4'>{pendingReservations.map(renderReservationCard)}</div>

                            {/* Budgets Section */}
                            {budgets.length > 0 && (
                                <div className='space-y-4'>
                                    <CardHeader className='px-0'>
                                        <CardTitle>Presupuestos</CardTitle>
                                    </CardHeader>
                                    {budgets.map(renderReservationCard)}
                                </div>
                            )}
                        </>
                    )}

                    {/* Delete Confirmation Dialog */}
                    <ConfirmationDialog
                        isOpen={!!reservationToDelete}
                        onClose={() => setReservationToDelete(null)}
                        onConfirm={() => handleDeleteBudget(reservationToDelete!)}
                        title='Eliminar presupuesto'
                        description='¿Estás seguro de que quieres eliminar este presupuesto? Esta acción no se puede deshacer.'
                        confirmText='Eliminar'
                        variant='destructive'
                    />

                    {/* Accept Confirmation Dialog */}
                    <ConfirmationDialog
                        isOpen={!!reservationToAccept}
                        onClose={() => setReservationToAccept(null)}
                        onConfirm={() => handleAcceptBudget(reservationToAccept!)}
                        title='Aceptar presupuesto'
                        description='¿Quieres aceptar este presupuesto?'
                        confirmText='Aceptar'
                    />
                </>
            )}
        </div>
    )
}
